import React, { createContext, useContext, useState } from 'react';
import { PromptType, PromptValues } from '@alucio/aws-beacon-amplify/src/models';
import PromptValuesStateProvider, {
  usePromptValuesState,
  PromptValuesStateType,
} from '../PromptValues/PromptValuesStateProvider';
import { ComplianceRuleStatus } from '../../../types/enums';

export interface ComplianceRuleStatusData {
  id: string;
  text: string;
  status: ComplianceRuleStatus;
  explanation?: string;
  enabled: boolean;
}

export interface ComplianceResultsContextType {
  results: ComplianceRuleStatusData[];
  setResults: React.Dispatch<React.SetStateAction<ComplianceRuleStatusData[]>>;
  failedCount: number;
}

const ComplianceResultsContext = createContext<ComplianceResultsContextType | null>(null);

export const useComplianceResults = (): ComplianceResultsContextType => {
  const context = useContext(ComplianceResultsContext);
  if (!context) {
    throw new Error('useComplianceResults must be used within ComplianceStateProvider');
  }
  return context;
};

export interface ComplianceStateType extends PromptValuesStateType {
  rules: PromptValues[];
  handleCreateRule: (text: string) => void;
  handleUpdateRule: (rule: PromptValues, updatedText: string) => void;
  handleDeleteRule: (rule: PromptValues) => void;
}

export const useComplianceState = (): ComplianceStateType => {
  const state = usePromptValuesState();

  return {
    ...state,
    rules: state.values,
    handleCreateRule: state.handleCreate,
    handleUpdateRule: state.handleUpdate,
    handleDeleteRule: state.handleDelete,
  };
};

const ComplianceResultsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [results, setResults] = useState<ComplianceRuleStatusData[]>([]);

  const failedCount = results.filter(rule => rule.status === ComplianceRuleStatus.FAILED && rule.enabled).length;

  return (
    <ComplianceResultsContext.Provider value={{ results, setResults, failedCount }}>
      {children}
    </ComplianceResultsContext.Provider>
  );
};

const ComplianceStateProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  return (
    <PromptValuesStateProvider promptType={PromptType.DECK_COMPLIANCE_RULE}>
      <ComplianceResultsProvider>
        {children}
      </ComplianceResultsProvider>
    </PromptValuesStateProvider>
  );
};

export default ComplianceStateProvider;
